import React from 'react';
import { Award, ShieldCheck, Users, Leaf, Clock, Euro } from 'lucide-react';
import { companyInfo, whyChooseUs } from '../mock';

const iconMap = {
  award: Award,
  'shield-check': ShieldCheck,
  users: Users,
  leaf: Leaf,
  clock: Clock,
  euro: Euro
};

const About = () => {
  const getIcon = (iconName) => {
    const Icon = iconMap[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* About company */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Über MC MaxClean OG
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#2563EB] to-[#1E40AF] rounded-full" />
            <p className="text-lg text-gray-700 leading-relaxed">
              MC MaxClean OG ist ein neu gegründetes Unternehmen (2025) in 
              Feldkirchen bei Graz, das auf der langjährigen Erfahrung und 
              Expertise im Bereich Hausbetreuung und Reinigungsdienste aufbaut.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Nach Jahren wertvoller Erfahrung in der Branche haben wir unter der 
              Leitung von <strong>{companyInfo.manager}</strong> beschlossen, unser 
              eigenes Unternehmen zu gründen. Diese Entscheidung ermöglicht es uns, 
              unsere bewährten Standards und unser Fachwissen in den Bereichen 
              Hausbetreuung, Winterdienst, Grünflächenpflege, Entsorgung und 
              Ressourcenmanagement direkt an unsere Kunden weiterzugeben.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Mit frischer Energie und fundiertem Know-how bieten wir maßgeschneiderte 
              Lösungen für Privat- und Gewerbekunden. Unser Ziel ist es, durch 
              persönlichen Service und höchste Qualitätsstandards langfristige 
              Partnerschaften aufzubauen.
            </p>
            <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#1E40AF]/10 rounded-2xl p-6 border border-[#2563EB]/20">
              <p className="text-gray-800 italic">
                "Nach vielen Jahren Erfahrung in der Branche haben wir uns 
                entschieden, unser eigenes Unternehmen zu gründen. Mit MC MaxClean OG 
                bringen wir bewährte Expertise und frische Perspektiven zusammen, 
                um unseren Kunden den bestmöglichen Service zu bieten."
              </p>
              <p className="text-[#1E40AF] font-semibold mt-3">
                — {companyInfo.manager}, Gründer & Geschäftsführer
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1627905646269-7f034dcc5738?w=600&q=80"
                alt="Professional team"
                className="rounded-2xl shadow-xl h-64 w-full object-cover"
              />
              <img
                src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=600&q=80"
                alt="Clean office"
                className="rounded-2xl shadow-xl h-64 w-full object-cover mt-8"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-[#2563EB] to-[#1E40AF] rounded-2xl opacity-20 blur-2xl" />
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#1E40AF] to-[#2563EB] rounded-2xl opacity-20 blur-2xl" />
          </div>
        </div>

        {/* Why choose us */}
        <div className="text-center mb-12">
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Warum MC MaxClean OG?
          </h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Entdecken Sie die Vorteile, die uns zu Ihrem idealen Partner machen
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {whyChooseUs.map((item, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-[#2563EB]/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              style={{
                animationDelay: `${index * 0.1}s`,
                animation: 'fadeInUp 0.6s ease-out forwards',
                opacity: 0
              }}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#2563EB] to-[#1E40AF] rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform shadow-lg">
                {getIcon(item.icon)}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h4>
              <p className="text-gray-600 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
